<template>
    <div class="manifest-mapping">
        <div class="manifest-mapping__views" role="tablist" aria-label="Mapping configuration">
            <button
                type="button"
                role="tab"
                class="manifest-mapping__view"
                :class="{'manifest-mapping__view--active': activeView === 'columns'}"
                :aria-selected="activeView === 'columns' ? 'true' : 'false'"
                @click="activeView = 'columns'"
            >
                Column mappings
                <b-badge variant="light-secondary" class="ml-50">
                    {{ value.rows.length }}
                </b-badge>
            </button>
            <button
                type="button"
                role="tab"
                class="manifest-mapping__view"
                :class="{'manifest-mapping__view--active': activeView === 'record'}"
                :aria-selected="activeView === 'record' ? 'true' : 'false'"
                @click="activeView = 'record'"
            >
                Record pipeline
                <b-badge variant="light-secondary" class="ml-50">
                    {{ recordPipeline.length }}
                </b-badge>
            </button>
        </div>

        <template v-if="activeView === 'columns'">
            <div class="manifest-mapping__toolbar">
            <div class="text-muted">
                {{ mappedCount }} canonical {{ mappedCount === 1 ? 'field' : 'fields' }} mapped
                · {{ referencedColumnCount }} source {{ referencedColumnCount === 1 ? 'column' : 'columns' }} referenced
            </div>

            <div class="manifest-mapping__toolbar-controls">
                <b-button-group size="sm">
                    <b-button
                        v-for="filterOption in filterOptions"
                        :key="filterOption.value"
                        :variant="filter === filterOption.value ? 'primary' : 'outline-secondary'"
                        @click="filter = filterOption.value"
                    >
                        {{ filterOption.label }}
                    </b-button>
                </b-button-group>
                <b-form-input
                    v-model.trim="search"
                    size="sm"
                    placeholder="Search columns or fields"
                    class="manifest-mapping__search"
                />
                <b-button
                    size="sm"
                    variant="outline-primary"
                    :disabled="disabled || !sourceColumns.length"
                    @click="addMapping"
                >
                    <feather-icon icon="PlusIcon" class="mr-50" />
                    Add mapping
                </b-button>
            </div>
            </div>

            <div class="manifest-mapping__requirements">
            <div class="manifest-mapping__requirements-heading">
                <div class="manifest-mapping__requirements-title">
                    <h5 class="mb-0">Mapping requirements</h5>
                    <span class="text-muted">
                        Resolved by the backend from the selected driver configuration.
                    </span>
                </div>
                <b-badge :variant="requirementsSatisfied ? 'light-success' : 'light-warning'">
                    {{ satisfiedRequirementCount }} of {{ requirementStatuses.length }} complete
                </b-badge>
            </div>

            <div class="manifest-mapping__requirement-list">
                <div
                    v-for="requirement in requirementStatuses"
                    :key="requirement.key"
                    class="manifest-mapping__requirement"
                    :class="{
                        'manifest-mapping__requirement--satisfied': requirement.satisfied,
                    }"
                >
                    <button
                        type="button"
                        class="manifest-mapping__requirement-header"
                        :aria-expanded="isRequirementExpanded(requirement) ? 'true' : 'false'"
                        :aria-controls="requirementDetailsId(requirement)"
                        @click="toggleRequirement(requirement)"
                    >
                        <span class="manifest-mapping__requirement-icon">
                            <feather-icon
                                :icon="requirement.satisfied ? 'CheckIcon' : 'AlertCircleIcon'"
                                size="15"
                            />
                        </span>
                        <span class="manifest-mapping__requirement-title font-weight-bold">
                            {{ requirement.title }}
                        </span>
                        <small class="manifest-mapping__requirement-summary text-muted">
                            {{ requirement.description }}
                        </small>
                        <span
                            class="manifest-mapping__requirement-count"
                            :class="{'manifest-mapping__requirement-count--incomplete': !requirement.satisfied}"
                        >
                            {{ requirement.mappedCount }} / {{ requirement.minimumMappedFields }}
                        </span>
                        <feather-icon
                            :icon="isRequirementExpanded(requirement) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                            size="17"
                            class="manifest-mapping__requirement-chevron"
                        />
                    </button>

                    <div
                        v-if="isRequirementExpanded(requirement)"
                        :id="requirementDetailsId(requirement)"
                        class="manifest-mapping__requirement-details"
                    >
                        <template v-if="!requirement.satisfied">
                            <div class="mb-50 text-muted">
                                {{ requirement.description }}
                            </div>
                            <div class="mb-50 font-weight-bold">
                                {{ missingFieldsLead(requirement) }}
                            </div>
                            <div class="manifest-mapping__missing-fields">
                                <span
                                    v-for="field in requirement.missingFields"
                                    :key="field.key"
                                    class="manifest-mapping__missing-field"
                                >
                                    <span>{{ field.title }}</span>
                                    <code>{{ field.key }}</code>
                                </span>
                            </div>
                        </template>
                        <div v-else class="text-success">
                            <feather-icon icon="CheckIcon" size="15" class="mr-25" />
                            All fields required by this group are mapped.
                        </div>
                    </div>
                </div>
            </div>
            </div>

            <div class="manifest-mapping__layout">
            <div class="manifest-mapping__list">
                <b-table
                    hover
                    responsive
                    show-empty
                    :items="filteredRows"
                    :fields="tableFields"
                    :tbody-tr-class="rowClass"
                    empty-text="No mappings match the current filter."
                    class="manifest-mapping__table mb-0"
                    @row-clicked="selectRow"
                >
                    <template #cell(source)="data">
                        <div class="font-weight-bold">{{ sourceTitle(data.item) }}</div>
                        <small class="text-muted">
                            {{ sourceSubtitle(data.item) }}
                        </small>
                    </template>

                    <template #cell(examples)="data">
                        <span
                            v-if="sourceExampleSummary(data.item)"
                            class="manifest-mapping__examples"
                            :title="sourceExampleSummary(data.item)"
                        >
                            {{ sourceExampleSummary(data.item) }}
                        </span>
                        <span v-else class="text-muted">No sample values</span>
                    </template>

                    <template #cell(target)="data">
                        <template v-if="targetField(data.item)">
                            <div>{{ targetField(data.item).title }}</div>
                            <small class="text-muted">{{ targetField(data.item).key }}</small>
                        </template>
                        <span v-else class="text-muted">Not mapped</span>
                    </template>

                    <template #cell(pipeline)="data">
                        <template v-if="data.item.pipeline.length">
                            <b-badge
                                v-for="(step, stepIndex) in data.item.pipeline"
                                :key="pipelineStepKey(step, stepIndex)"
                                variant="light-secondary"
                                class="mr-25"
                            >
                                {{ componentTitle(step) }}
                            </b-badge>
                        </template>
                        <span v-else class="text-muted">No steps</span>
                    </template>

                    <template #cell(status)="data">
                        <b-badge :variant="rowStatus(data.item).variant">
                            {{ rowStatus(data.item).label }}
                        </b-badge>
                    </template>
                </b-table>
            </div>

            <aside class="manifest-mapping__details">
                <template v-if="selectedRow">
                    <div class="d-flex align-items-start justify-content-between mb-2">
                        <div>
                            <div class="text-muted font-small-2 text-uppercase mb-25">
                                Source value
                            </div>
                            <h4 class="mb-0">{{ sourceTitle(selectedRow) }}</h4>
                        </div>
                        <b-button
                            v-if="selectedRow.additional"
                            size="sm"
                            variant="flat-danger"
                            title="Remove mapping"
                            :disabled="disabled"
                            @click="removeSelectedMapping"
                        >
                            <feather-icon icon="Trash2Icon" />
                        </b-button>
                    </div>

                    <b-form-group label="Extractor" label-for="manifest-mapping-extractor">
                        <v-select
                            id="manifest-mapping-extractor"
                            :value="selectedSourceExtractor"
                            :options="sourceExtractors"
                            :clearable="false"
                            :disabled="disabled"
                            label="title"
                            @input="updateSourceExtractor"
                        >
                            <template #option="extractor">
                                <div>{{ extractor.title }}</div>
                                <small class="text-muted">{{ extractor.description }}</small>
                            </template>
                            <template #selected-option="extractor">
                                {{ extractor.title }}
                            </template>
                        </v-select>
                    </b-form-group>

                    <div
                        v-if="selectedSourceExtractor"
                        class="manifest-mapping__extractor-info text-muted"
                    >
                        {{ selectedSourceExtractor.description }}
                    </div>

                    <template v-if="usesFallbackColumns">
                        <div class="manifest-mapping__source-heading">
                            <label class="mb-25">Fallback columns</label>
                            <small class="text-muted">
                                Checked from top to bottom. The first non-empty value is used.
                            </small>
                        </div>

                        <div class="manifest-mapping__source-list">
                            <div
                                v-for="(columnIndex, index) in selectedSourceColumnIndexes"
                                :key="`${selectedRow.id}:${index}`"
                                class="manifest-mapping__source-item"
                            >
                                <span class="manifest-mapping__source-priority">{{ index + 1 }}</span>
                                <v-select
                                    :value="sourceColumn(columnIndex)"
                                    :options="sourceColumnOptions(columnIndex)"
                                    :clearable="false"
                                    :disabled="disabled"
                                    label="candidateHeader"
                                    class="manifest-mapping__source-select"
                                    @input="updateSourceColumnAt(index, $event)"
                                >
                                    <template #option="column">
                                        <div>{{ columnTitle(column) }}</div>
                                        <small class="text-muted">Column {{ column.index + 1 }}</small>
                                    </template>
                                    <template #selected-option="column">
                                        {{ columnTitle(column) }}
                                    </template>
                                </v-select>
                                <div class="manifest-mapping__source-actions">
                                    <b-button
                                        size="sm"
                                        variant="flat-secondary"
                                        title="Move column up"
                                        :disabled="disabled || index === 0"
                                        @click="moveSourceColumn(index, -1)"
                                    >
                                        <feather-icon icon="ArrowUpIcon" size="16" />
                                    </b-button>
                                    <b-button
                                        size="sm"
                                        variant="flat-secondary"
                                        title="Move column down"
                                        :disabled="disabled || index === selectedSourceColumnIndexes.length - 1"
                                        @click="moveSourceColumn(index, 1)"
                                    >
                                        <feather-icon icon="ArrowDownIcon" size="16" />
                                    </b-button>
                                    <b-button
                                        size="sm"
                                        variant="flat-danger"
                                        title="Remove fallback column"
                                        :disabled="disabled || selectedSourceColumnIndexes.length === 1"
                                        @click="removeSourceColumn(index)"
                                    >
                                        <feather-icon icon="Trash2Icon" size="16" />
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <div class="manifest-mapping__source-add">
                            <b-button
                                size="sm"
                                variant="outline-primary"
                                :disabled="disabled || !nextAvailableSourceColumn"
                                @click="addSourceColumn"
                            >
                                <feather-icon
                                    v-if="nextAvailableSourceColumn"
                                    icon="PlusIcon"
                                    class="mr-50"
                                />
                                {{ nextAvailableSourceColumn ? 'Add fallback column' : 'All columns added' }}
                            </b-button>
                        </div>
                    </template>

                    <b-form-group
                        v-else
                        label="Source column"
                        label-for="manifest-mapping-source"
                    >
                        <v-select
                            id="manifest-mapping-source"
                            :value="selectedSourceColumn"
                            :options="sourceColumns"
                            :clearable="false"
                            :disabled="disabled || !selectedRow.additional"
                            label="candidateHeader"
                            @input="updateSourceColumn"
                        >
                            <template #option="column">
                                <div>{{ columnTitle(column) }}</div>
                                <small class="text-muted">Column {{ column.index + 1 }}</small>
                            </template>
                            <template #selected-option="column">
                                {{ columnTitle(column) }}
                            </template>
                        </v-select>
                    </b-form-group>

                    <div class="manifest-mapping__target-section">
                        <b-form-group label="Canonical field" label-for="manifest-mapping-target">
                            <v-select
                                id="manifest-mapping-target"
                                :value="selectedTargetField"
                                :options="canonicalFields"
                                :selectable="isFieldSelectable"
                                :disabled="disabled"
                                label="title"
                                placeholder="Choose a canonical field"
                                @input="updateTargetField"
                            >
                                <template #option="field">
                                    <div class="d-flex align-items-center justify-content-between">
                                        <span>{{ field.title }}</span>
                                        <b-badge variant="light-secondary">{{ field.valueType }}</b-badge>
                                    </div>
                                    <small class="text-muted">{{ field.key }}</small>
                                </template>
                                <template #selected-option="field">
                                    {{ field.title }}
                                </template>
                            </v-select>
                        </b-form-group>

                        <div v-if="selectedTargetField" class="manifest-mapping__field-info">
                            <div class="d-flex align-items-center mb-50">
                                <b-badge variant="light-primary" class="mr-50">
                                    {{ selectedTargetField.valueType }}
                                </b-badge>
                                <code>{{ selectedTargetField.key }}</code>
                            </div>
                            <div class="text-muted">{{ selectedTargetField.description }}</div>
                        </div>
                    </div>

                    <div class="manifest-mapping__section">
                        <ManifestPipelineEditor
                            :value="selectedRow.pipeline"
                            :components="components"
                            :target-field="selectedTargetField"
                            :disabled="disabled"
                            @input="updatePipeline"
                        />
                    </div>

                    <div class="manifest-mapping__section">
                        <h5 class="mb-1">Sample values</h5>
                        <div v-if="selectedExampleGroups.length" class="manifest-mapping__sample-groups">
                            <div
                                v-for="group in selectedExampleGroups"
                                :key="group.index"
                                class="manifest-mapping__sample-group"
                            >
                                <div v-if="usesFallbackColumns" class="text-muted font-small-2 mb-50">
                                    {{ group.title }}
                                </div>
                                <div class="manifest-mapping__sample-list">
                                    <code v-for="(example, index) in group.examples" :key="index">
                                        {{ displayValue(example) }}
                                    </code>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-muted">No sample values were found.</div>
                    </div>
                </template>

                <div v-else class="manifest-mapping__empty-selection">
                    <feather-icon icon="MousePointerIcon" size="28" />
                    <div class="mt-1">Select a mapping to configure it.</div>
                </div>
            </aside>
            </div>
        </template>

        <section v-else class="manifest-mapping__record-pipeline" role="tabpanel">
            <ManifestPipelineEditor
                :value="recordPipeline"
                :components="recordComponents"
                :allowed-kinds="['record_transformer']"
                title="Processing steps"
                description="Ordered transformations applied to the complete canonical record after all field pipelines."
                picker-label="Record transformer"
                picker-placeholder="Choose a record transformer"
                empty-text="Add record transformers when the completed canonical record needs cross-field processing."
                initial-type="object"
                :allow-conditions="false"
                :requires-target-field="false"
                :show-target-type="false"
                :disabled="disabled"
                @input="updateRecordPipeline"
            />
        </section>
    </div>
</template>

<script>
import {
    BBadge,
    BButton,
    BButtonGroup,
    BFormGroup,
    BFormInput,
    BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ManifestPipelineEditor from './ManifestPipelineEditor.vue'

const COLUMN_BY_HEADER_EXTRACTOR = 'system.import.extractor.column_by_header'
const FIRST_PRESENT_COLUMN_EXTRACTOR = 'system.import.extractor.first_present_column'
const SUPPORTED_SOURCE_EXTRACTORS = [
    COLUMN_BY_HEADER_EXTRACTOR,
    FIRST_PRESENT_COLUMN_EXTRACTOR,
]

export default {
    components: {
        BBadge,
        BButton,
        BButtonGroup,
        BFormGroup,
        BFormInput,
        BTable,
        ManifestPipelineEditor,
        vSelect,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        sourceTab: {
            type: Object,
            required: true,
        },
        canonicalFields: {
            type: Array,
            default: () => [],
        },
        components: {
            type: Array,
            default: () => [],
        },
        recordComponents: {
            type: Array,
            default: () => [],
        },
        requirements: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeView: 'columns',
            filter: 'all',
            filterOptions: [
                {value: 'all', label: 'All'},
                {value: 'mapped', label: 'Mapped'},
                {value: 'unmapped', label: 'Unmapped'},
            ],
            search: '',
            expandedRequirementKeys: [],
            tableFields: [
                {key: 'source', label: 'Source value'},
                {key: 'examples', label: 'Examples'},
                {key: 'target', label: 'Canonical field'},
                {key: 'pipeline', label: 'Pipeline'},
                {key: 'status', label: ''},
            ],
        }
    },
    computed: {
        sourceColumns() {
            return this.sourceTab && Array.isArray(this.sourceTab.columns)
                ? this.sourceTab.columns
                : []
        },
        sourceExtractors() {
            return this.components.filter(component => (
                component.kind === 'value_extractor'
                && component.status !== 'deprecated'
                && SUPPORTED_SOURCE_EXTRACTORS.includes(component.key)
            ))
        },
        mappedCount() {
            return this.value.rows.filter(row => row.targetFieldKey).length
        },
        referencedColumnIndexes() {
            return new Set(this.value.rows.reduce((indexes, row) => (
                row.targetFieldKey
                    ? [...indexes, ...this.effectiveSourceIndexes(row)]
                    : indexes
            ), []))
        },
        referencedColumnCount() {
            return this.referencedColumnIndexes.size
        },
        recordPipeline() {
            return Array.isArray(this.value.recordPipeline) ? this.value.recordPipeline : []
        },
        requirementStatuses() {
            const mappedFields = new Set(
                this.value.rows
                    .filter(row => row.targetFieldKey)
                    .map(row => row.targetFieldKey)
            )

            return this.requirements.map(requirement => {
                const fields = Array.isArray(requirement.fields) ? requirement.fields : []
                const mappedCount = fields.filter(field => mappedFields.has(field)).length
                const minimumMappedFields = Number(requirement.minimumMappedFields) || 0
                const missingFields = fields
                    .filter(field => !mappedFields.has(field))
                    .map(key => {
                        const canonicalField = this.canonicalFields.find(field => field.key === key)

                        return {
                            key,
                            title: canonicalField ? canonicalField.title : key,
                        }
                    })

                return {
                    ...requirement,
                    mappedCount,
                    minimumMappedFields,
                    missingFields,
                    satisfied: mappedCount >= minimumMappedFields,
                }
            })
        },
        satisfiedRequirementCount() {
            return this.requirementStatuses.filter(requirement => requirement.satisfied).length
        },
        requirementsSatisfied() {
            return this.requirementStatuses.every(requirement => requirement.satisfied)
        },
        filteredRows() {
            const search = this.search.toLowerCase()

            return this.value.rows.filter(row => {
                if (this.filter === 'mapped' && !row.targetFieldKey) {
                    return false
                }
                if (this.filter === 'unmapped' && row.targetFieldKey) {
                    return false
                }
                if (!search) {
                    return true
                }

                const target = this.targetField(row)
                return [
                    this.sourceTitle(row),
                    target ? target.title : '',
                    target ? target.key : '',
                ].some(value => value.toLowerCase().includes(search))
            })
        },
        selectedRow() {
            return this.value.rows.find(row => row.id === this.value.selectedRowId) || null
        },
        selectedSourceColumn() {
            if (!this.selectedRow) {
                return null
            }

            return this.sourceColumn(this.selectedSourceColumnIndexes[0])
        },
        selectedSourceColumnIndexes() {
            return this.selectedRow ? this.sourceIndexes(this.selectedRow) : []
        },
        selectedSourceExtractor() {
            if (!this.selectedRow) {
                return null
            }

            const key = this.selectedRow.extractorKey || COLUMN_BY_HEADER_EXTRACTOR
            const version = Number.isInteger(this.selectedRow.extractorVersion)
                ? this.selectedRow.extractorVersion
                : null

            return this.sourceExtractors.find(extractor => (
                extractor.key === key
                && (version === null || extractor.version === version)
            )) || null
        },
        usesFallbackColumns() {
            return Boolean(
                this.selectedSourceExtractor
                && this.selectedSourceExtractor.key === FIRST_PRESENT_COLUMN_EXTRACTOR
            )
        },
        nextAvailableSourceColumn() {
            const selected = new Set(this.selectedSourceColumnIndexes)
            return this.sourceColumns.find(column => !selected.has(column.index)) || null
        },
        selectedTargetField() {
            return this.selectedRow ? this.targetField(this.selectedRow) : null
        },
        selectedExampleGroups() {
            return this.selectedRow ? this.sourceExampleGroups(this.selectedRow) : []
        },
    },
    methods: {
        emitRows(rows, selectedRowId = this.value.selectedRowId) {
            this.$emit('input', {
                ...this.value,
                rows,
                selectedRowId,
            })
        },
        selectRow(row) {
            this.$emit('input', {
                ...this.value,
                selectedRowId: row.id,
            })
        },
        addMapping() {
            if (this.disabled || !this.sourceColumns.length) {
                return
            }

            const id = `additional-${Date.now()}`
            const defaultExtractor = this.sourceExtractors.find(extractor => (
                extractor.key === COLUMN_BY_HEADER_EXTRACTOR
            )) || this.sourceExtractors[0] || null
            const row = {
                id,
                sourceColumnIndex: this.sourceColumns[0].index,
                sourceColumnIndexes: [this.sourceColumns[0].index],
                extractorKey: defaultExtractor
                    ? defaultExtractor.key
                    : COLUMN_BY_HEADER_EXTRACTOR,
                extractorVersion: defaultExtractor
                    ? defaultExtractor.version
                    : 1,
                targetFieldKey: null,
                pipeline: [],
                additional: true,
            }

            this.filter = 'all'
            this.emitRows([...this.value.rows, row], id)
        },
        removeSelectedMapping() {
            if (!this.selectedRow || !this.selectedRow.additional || this.disabled) {
                return
            }

            const rows = this.value.rows.filter(row => row.id !== this.selectedRow.id)
            this.emitRows(rows, rows.length ? rows[0].id : null)
        },
        updateSourceColumn(column) {
            if (!column || !this.selectedRow || !this.selectedRow.additional) {
                return
            }

            this.updateSelectedRow({
                sourceColumnIndex: column.index,
                sourceColumnIndexes: [column.index],
            })
        },
        updateSourceExtractor(extractor) {
            if (!extractor || !this.selectedRow || this.disabled) {
                return
            }

            const indexes = this.selectedSourceColumnIndexes.length
                ? this.selectedSourceColumnIndexes
                : this.sourceColumns.slice(0, 1).map(column => column.index)
            this.updateSelectedRow({
                extractorKey: extractor.key,
                extractorVersion: extractor.version,
                sourceColumnIndex: indexes[0],
                sourceColumnIndexes: indexes,
            })
        },
        sourceColumnOptions(currentIndex) {
            const selected = new Set(this.selectedSourceColumnIndexes)
            return this.sourceColumns.filter(column => (
                column.index === currentIndex || !selected.has(column.index)
            ))
        },
        updateSourceColumnAt(position, column) {
            if (!column || !this.selectedRow || this.disabled) {
                return
            }

            const indexes = [...this.selectedSourceColumnIndexes]
            indexes.splice(position, 1, column.index)
            this.updateSourceIndexes(indexes)
        },
        addSourceColumn() {
            if (!this.nextAvailableSourceColumn || this.disabled) {
                return
            }

            this.updateSourceIndexes([
                ...this.selectedSourceColumnIndexes,
                this.nextAvailableSourceColumn.index,
            ])
        },
        removeSourceColumn(position) {
            if (this.disabled || this.selectedSourceColumnIndexes.length === 1) {
                return
            }

            const indexes = this.selectedSourceColumnIndexes.filter((item, index) => (
                index !== position
            ))
            this.updateSourceIndexes(indexes)
        },
        moveSourceColumn(position, offset) {
            const target = position + offset
            if (this.disabled || target < 0 || target >= this.selectedSourceColumnIndexes.length) {
                return
            }

            const indexes = [...this.selectedSourceColumnIndexes]
            const [moved] = indexes.splice(position, 1)
            indexes.splice(target, 0, moved)
            this.updateSourceIndexes(indexes)
        },
        updateSourceIndexes(indexes) {
            if (!indexes.length) {
                return
            }

            this.updateSelectedRow({
                sourceColumnIndex: indexes[0],
                sourceColumnIndexes: indexes,
                sourceHeader: this.columnTitle(this.sourceColumn(indexes[0])),
            })
        },
        updateTargetField(field) {
            if (!this.selectedRow) {
                return
            }

            this.updateSelectedRow({
                targetFieldKey: field ? field.key : null,
                pipeline: [],
            })
        },
        updateSelectedRow(patch) {
            const rows = this.value.rows.map(row => (
                row.id === this.selectedRow.id ? {...row, ...patch} : row
            ))
            this.emitRows(rows)
        },
        updatePipeline(pipeline) {
            this.updateSelectedRow({pipeline})
        },
        updateRecordPipeline(recordPipeline) {
            if (this.disabled) {
                return
            }

            this.$emit('input', {
                ...this.value,
                recordPipeline,
            })
        },
        sourceColumn(index) {
            return this.sourceColumns.find(column => column.index === index) || null
        },
        sourceIndexes(row) {
            if (row && Array.isArray(row.sourceColumnIndexes) && row.sourceColumnIndexes.length) {
                return row.sourceColumnIndexes
            }

            return row && Number.isInteger(row.sourceColumnIndex)
                ? [row.sourceColumnIndex]
                : []
        },
        rowUsesFallbackColumns(row) {
            return Boolean(row && row.extractorKey === FIRST_PRESENT_COLUMN_EXTRACTOR)
        },
        effectiveSourceIndexes(row) {
            return this.rowUsesFallbackColumns(row)
                ? this.sourceIndexes(row)
                : this.sourceIndexes(row).slice(0, 1)
        },
        sourceTitle(row) {
            const indexes = this.effectiveSourceIndexes(row)

            return indexes
                .map(index => this.columnTitle(this.sourceColumn(index)))
                .join(' → ')
                || 'Unknown source'
        },
        sourceSubtitle(row) {
            const indexes = this.effectiveSourceIndexes(row)
            if (indexes.length > 1) {
                return `${indexes.length} columns · priority order`
            }

            return indexes.length ? `Column ${indexes[0] + 1}` : 'No source column'
        },
        columnTitle(column) {
            if (!column) {
                return 'Unknown column'
            }

            return column.candidateHeader || `Column ${column.index + 1}`
        },
        sourceExamples(row) {
            return this.sourceExampleGroups(row).reduce(
                (examples, group) => [...examples, ...group.examples],
                []
            )
        },
        sourceExampleGroups(row) {
            const indexes = this.effectiveSourceIndexes(row)

            return indexes.map(index => {
                const column = this.sourceColumn(index)
                return {
                    index,
                    title: this.columnTitle(column),
                    examples: column && Array.isArray(column.examples)
                        ? column.examples
                        : [],
                }
            }).filter(group => group.examples.length)
        },
        sourceExampleSummary(row) {
            const groups = this.sourceExampleGroups(row)
            if (!groups.length) {
                return ''
            }
            if (!this.rowUsesFallbackColumns(row)) {
                return groups[0].examples.join(', ')
            }

            return groups.map(group => (
                `${group.title}: ${group.examples.slice(0, 2).join(', ')}`
            )).join(' · ')
        },
        rowStatus(row) {
            if (row.targetFieldKey) {
                return {label: 'Mapped', variant: 'light-success'}
            }
            if (this.referencedColumnIndexes.has(row.sourceColumnIndex)) {
                return {label: 'Referenced', variant: 'light-info'}
            }

            return {label: 'Unmapped', variant: 'light-secondary'}
        },
        targetField(row) {
            return this.canonicalFields.find(field => field.key === row.targetFieldKey) || null
        },
        isFieldSelectable(field) {
            if (!field || !this.selectedRow) {
                return false
            }

            return !this.value.rows.some(row => (
                row.id !== this.selectedRow.id && row.targetFieldKey === field.key
            ))
        },
        requirementDetailsId(requirement) {
            return `manifest-mapping-requirement-${this._uid}-${requirement.key}`
        },
        isRequirementExpanded(requirement) {
            return this.expandedRequirementKeys.includes(requirement.key)
        },
        toggleRequirement(requirement) {
            if (this.isRequirementExpanded(requirement)) {
                this.expandedRequirementKeys = this.expandedRequirementKeys
                    .filter(key => key !== requirement.key)
                return
            }

            this.expandedRequirementKeys = [
                ...this.expandedRequirementKeys,
                requirement.key,
            ]
        },
        missingFieldsLead(requirement) {
            const remainingCount = Math.max(
                requirement.minimumMappedFields - requirement.mappedCount,
                0
            )

            if (requirement.kind === 'at_least_one') {
                return remainingCount === 1
                    ? 'Map at least one of these fields:'
                    : `Map at least ${remainingCount} of these fields:`
            }

            return remainingCount === 1
                ? 'Map this field:'
                : `Map these ${remainingCount} fields:`
        },
        componentTitle(step) {
            const conditional = step && typeof step === 'object' && !step.key && step.when
            const reference = conditional ? step.when : step
            const key = typeof reference === 'string' ? reference : reference.key
            const version = typeof reference === 'string' ? null : reference.version
            const component = this.components.find(item => (
                item.key === key && (version === null || item.version === version)
            ))
            const title = component ? component.title : key
            return conditional ? `If ${title}` : title
        },
        pipelineStepKey(step, index) {
            const conditional = step && typeof step === 'object' && !step.key && step.when
            const reference = conditional ? step.when : step
            const key = typeof reference === 'string' ? reference : reference.key
            const version = typeof reference === 'string' ? 'legacy' : reference.version
            return `${key}@${version}:${index}`
        },
        displayValue(value) {
            if (value === null) {
                return 'null'
            }
            if (value === '') {
                return '(empty)'
            }

            return String(value)
        },
        rowClass(row) {
            return row && row.id === this.value.selectedRowId
                ? 'manifest-mapping__row--selected'
                : ''
        },
    },
}
</script>

<style lang="scss" scoped>
.manifest-mapping {
    margin: -1.5rem;
}

.manifest-mapping__views {
    display: flex;
    padding: 0 1.5rem;
    border-bottom: 1px solid #ebe9f1;
    gap: 1.5rem;
}

.manifest-mapping__view {
    display: inline-flex;
    align-items: center;
    padding: .85rem 0 .7rem;
    border: 0;
    border-bottom: 2px solid transparent;
    color: #82868b;
    background: transparent;
    font-weight: 500;
}

.manifest-mapping__view:hover,
.manifest-mapping__view:focus {
    color: var(--primary);
    outline: none;
}

.manifest-mapping__view--active {
    border-bottom-color: var(--primary);
    color: var(--primary);
}

.manifest-mapping__record-pipeline {
    min-height: 31rem;
    padding: 1.5rem;
}

.manifest-mapping__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #ebe9f1;
}

.manifest-mapping__requirements {
    padding: .75rem 1.5rem;
    border-bottom: 1px solid #ebe9f1;
    background: rgba(0, 0, 0, .02);
}

.manifest-mapping__requirements-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: .5rem;
}

.manifest-mapping__requirements-title {
    display: flex;
    min-width: 0;
    align-items: baseline;
    gap: .75rem;
}

.manifest-mapping__requirements-title .text-muted {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.manifest-mapping__requirement-list {
    overflow: hidden;
    border: 1px solid #dedce5;
    border-radius: .357rem;
    background: #fff;
}

.manifest-mapping__requirement {
    border-left: 3px solid #ff9f43;
}

.manifest-mapping__requirement + .manifest-mapping__requirement {
    border-top: 1px solid #ebe9f1;
}

.manifest-mapping__requirement--satisfied {
    border-left-color: #28c76f;
}

.manifest-mapping__requirement-header {
    display: grid;
    width: 100%;
    grid-template-columns: 1.5rem auto minmax(0, 1fr) auto 1rem;
    align-items: center;
    gap: .65rem;
    padding: .55rem .75rem;
    border: 0;
    color: inherit;
    background: transparent;
    text-align: left;
}

.manifest-mapping__requirement-header:hover {
    background: rgba(0, 0, 0, .025);
}

.manifest-mapping__requirement-header:focus {
    outline: none;
}

.manifest-mapping__requirement-header:focus-visible {
    box-shadow: inset 0 0 0 2px rgba(115, 103, 240, .22);
}

.manifest-mapping__requirement-icon {
    display: flex;
    width: 1.5rem;
    height: 1.5rem;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    background: #ff9f43;
}

.manifest-mapping__requirement--satisfied .manifest-mapping__requirement-icon {
    background: #28c76f;
}

.manifest-mapping__requirement-title,
.manifest-mapping__requirement-summary {
    min-width: 0;
}

.manifest-mapping__requirement-summary {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.manifest-mapping__requirement-count {
    font-weight: 600;
    white-space: nowrap;
}

.manifest-mapping__requirement-count--incomplete {
    color: #ff9f43;
}

.manifest-mapping__requirement-chevron {
    color: #82868b;
}

.manifest-mapping__requirement-details {
    padding: .75rem 1rem .9rem 2.9rem;
    border-top: 1px solid #ebe9f1;
    background: rgba(0, 0, 0, .015);
}

.manifest-mapping__missing-fields {
    display: flex;
    flex-wrap: wrap;
    gap: .5rem;
}

.manifest-mapping__missing-field {
    display: inline-flex;
    flex-direction: column;
    padding: .4rem .6rem;
    border-radius: .357rem;
    background: rgba(255, 159, 67, .1);
}

.manifest-mapping__toolbar-controls {
    display: flex;
    align-items: center;
    gap: .75rem;
}

.manifest-mapping__search {
    width: 15rem;
}

.manifest-mapping__layout {
    display: flex;
    min-height: 31rem;
}

.manifest-mapping__list {
    min-width: 0;
    flex: 1 1 auto;
}

.manifest-mapping__details {
    flex: 0 0 26rem;
    padding: 1.5rem;
    border-left: 1px solid #ebe9f1;
}

.manifest-mapping__table {
    ::v-deep tbody tr {
        cursor: pointer;
    }

    ::v-deep .manifest-mapping__row--selected > td {
        background: rgba(0, 0, 0, .045);
    }

    ::v-deep th,
    ::v-deep td {
        vertical-align: middle;
    }
}

.manifest-mapping__examples {
    display: block;
    overflow: hidden;
    max-width: 13rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.manifest-mapping__field-info {
    padding: 1rem;
    border-radius: .357rem;
    background: rgba(0, 0, 0, .035);
}

.manifest-mapping__extractor-info {
    margin-top: -.5rem;
    margin-bottom: 1rem;
    padding: .75rem;
    border-radius: .357rem;
    background: rgba(0, 0, 0, .035);
}

.manifest-mapping__source-heading {
    display: flex;
    margin-bottom: .75rem;
    flex-direction: column;
}

.manifest-mapping__source-list {
    display: flex;
    margin-bottom: .75rem;
    flex-direction: column;
    gap: .5rem;
}

.manifest-mapping__source-item {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: .5rem;
}

.manifest-mapping__source-priority {
    display: inline-flex;
    width: 1.75rem;
    height: 1.75rem;
    flex: 0 0 1.75rem;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: var(--primary);
    background: rgba(115, 103, 240, .1);
    font-weight: 600;
}

.manifest-mapping__source-select {
    min-width: 0;
    flex: 1 1 auto;
}

.manifest-mapping__source-actions {
    display: flex;
    flex: 0 0 auto;
}

.manifest-mapping__source-actions .btn {
    padding-right: .35rem;
    padding-left: .35rem;
}

.manifest-mapping__source-add {
    display: flex;
    justify-content: flex-end;
}

.manifest-mapping__target-section {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid #ebe9f1;
}

.manifest-mapping__section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #ebe9f1;
}

.manifest-mapping__sample-list {
    display: flex;
    flex-wrap: wrap;
    gap: .5rem;
}

.manifest-mapping__sample-list code {
    display: block;
    max-width: 100%;
    overflow: hidden;
    padding: .4rem .6rem;
    border-radius: .25rem;
    background: rgba(0, 0, 0, .035);
    text-overflow: ellipsis;
    white-space: nowrap;
}

.manifest-mapping__sample-groups {
    display: flex;
    flex-direction: column;
    gap: .75rem;
}

.manifest-mapping__empty-selection {
    display: flex;
    min-height: 20rem;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: var(--manifest-secondary-text-color, #45434c);
    text-align: center;
}

@media (max-width: 1199.98px) {
    .manifest-mapping__toolbar,
    .manifest-mapping__toolbar-controls {
        align-items: stretch;
        flex-direction: column;
    }

    .manifest-mapping__search {
        width: auto;
    }

    .manifest-mapping__layout {
        display: block;
    }

    .manifest-mapping__details {
        border-top: 1px solid #ebe9f1;
        border-left: 0;
    }
}
</style>
